import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Member } from '../types';
import {
  User,
  Flag,
  Globe,
  CheckCircle,
  AlertTriangle,
  Calendar,
  MessageSquare,
  ClipboardList,
  FileText,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Shield,
  DollarSign,
  Activity,
  Heart,
  Phone,
  Stethoscope,
  Bell,
  FileCheck,
  Building2,
  CreditCard,
  TrendingUp,
  Users,
  Pill,
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';

interface Eligibility {
  id: string;
  coverage_type: string;
  plan_name: string;
  subscriber_id: string;
  eligibility_status: string;
  effective_date: string;
  termination_date: string | null;
  special_needs: string[];
}

interface Claim {
  id: string;
  claim_number: string;
  claim_type: string;
  service_date: string;
  provider_name: string;
  diagnosis_codes: string[];
  procedure_codes: string[];
  billed_amount: number;
  paid_amount: number;
  claim_status: string;
}

interface InpatientStay {
  id: string;
  admission_date: string;
  discharge_date: string;
  length_of_stay: number;
  facility_name: string;
  principal_diagnosis: string;
  admission_type: string;
  is_readmission: boolean;
}

interface EDVisit {
  id: string;
  visit_date: string;
  facility_name: string;
  chief_complaint: string;
  disposition: string;
  is_avoidable: boolean;
}

interface OfficeVisit {
  id: string;
  visit_date: string;
  provider_name: string;
  provider_type: string;
  visit_type: string;
  is_preventive: boolean;
}

interface Authorization {
  id: string;
  authorization_number: string;
  service_type: string;
  service_description: string;
  authorization_status: string;
  request_date: string;
  expiration_date: string | null;
  units_approved: number;
  units_used: number;
}

interface PreventiveCare {
  id: string;
  service_type: string;
  service_name: string;
  service_date: string | null;
  is_compliant: boolean;
  next_due_date: string | null;
  gap_in_care: boolean;
}

interface Alert {
  id: string;
  alert_type: string;
  alert_category: string;
  severity: string;
  title: string;
  description: string;
  status: string;
  created_at: string;
}

interface Interaction {
  id: string;
  interaction_type: string;
  interaction_date: string;
  staff_name: string;
  subject: string;
  notes: string;
  outcome: string;
}

interface EmergencyContact {
  id: string;
  contact_name: string;
  relationship: string;
  phone_cell: string;
  is_primary: boolean;
}

interface Provider {
  id: string;
  provider_type: string;
  provider_name: string;
  specialty: string;
  phone: string;
  is_primary: boolean;
  last_visit_date: string | null;
}

export default function Member360Comprehensive() {
  const { memberId } = useParams<{ memberId: string }>();
  const navigate = useNavigate();
  const [member, setMember] = useState<Member | null>(null);
  const [allMembers, setAllMembers] = useState<Member[]>([]);
  const [eligibility, setEligibility] = useState<Eligibility[]>([]);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [inpatientStays, setInpatientStays] = useState<InpatientStay[]>([]);
  const [edVisits, setEDVisits] = useState<EDVisit[]>([]);
  const [officeVisits, setOfficeVisits] = useState<OfficeVisit[]>([]);
  const [authorizations, setAuthorizations] = useState<Authorization[]>([]);
  const [preventiveCare, setPreventiveCare] = useState<PreventiveCare[]>([]);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [emergencyContacts, setEmergencyContacts] = useState<EmergencyContact[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('overview');

  useEffect(() => {
    fetchAllMembers();
  }, []);

  useEffect(() => {
    if (memberId) {
      fetchMemberData(memberId);
    }
  }, [memberId]);

  const fetchAllMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('members')
        .select('id, member_id, first_name, last_name')
        .order('member_id');

      if (error) throw error;
      setAllMembers(data || []);
    } catch (error) {
      console.error('Error fetching all members:', error);
    }
  };

  const fetchMemberData = async (memberIdParam: string) => {
    try {
      setLoading(true);
      const { data: memberData, error: memberError } = await supabase
        .from('members')
        .select('*')
        .eq('member_id', memberIdParam)
        .maybeSingle();

      if (memberError) throw memberError;
      setMember(memberData);

      if (memberData) {
        const [
          eligibilityResult,
          claimsResult,
          inpatientResult,
          edResult,
          officeResult,
          authsResult,
          preventiveResult,
          alertsResult,
          interactionsResult,
          emergencyContactsResult,
          providersResult,
        ] = await Promise.all([
          supabase
            .from('member_eligibility')
            .select('*')
            .eq('member_id', memberData.id),
          supabase
            .from('member_claims')
            .select('*')
            .eq('member_id', memberData.id)
            .order('service_date', { ascending: false })
            .limit(10),
          supabase
            .from('member_utilization_inpatient')
            .select('*')
            .eq('member_id', memberData.id)
            .order('admission_date', { ascending: false }),
          supabase
            .from('member_utilization_ed')
            .select('*')
            .eq('member_id', memberData.id)
            .order('visit_date', { ascending: false }),
          supabase
            .from('member_utilization_office')
            .select('*')
            .eq('member_id', memberData.id)
            .order('visit_date', { ascending: false })
            .limit(10),
          supabase
            .from('member_authorizations')
            .select('*')
            .eq('member_id', memberData.id)
            .order('request_date', { ascending: false }),
          supabase
            .from('member_preventive_care')
            .select('*')
            .eq('member_id', memberData.id)
            .order('service_date', { ascending: false }),
          supabase
            .from('member_alerts')
            .select('*')
            .eq('member_id', memberData.id)
            .eq('status', 'Active')
            .order('created_at', { ascending: false}),
          supabase
            .from('member_interactions')
            .select('*')
            .eq('member_id', memberData.id)
            .order('interaction_date', { ascending: false })
            .limit(10),
          supabase
            .from('member_emergency_contacts')
            .select('*')
            .eq('member_id', memberData.id),
          supabase
            .from('member_providers')
            .select('*')
            .eq('member_id', memberData.id),
        ]);

        if (eligibilityResult.data) setEligibility(eligibilityResult.data);
        if (claimsResult.data) setClaims(claimsResult.data);
        if (inpatientResult.data) setInpatientStays(inpatientResult.data);
        if (edResult.data) setEDVisits(edResult.data);
        if (officeResult.data) setOfficeVisits(officeResult.data);
        if (authsResult.data) setAuthorizations(authsResult.data);
        if (preventiveResult.data) setPreventiveCare(preventiveResult.data);
        if (alertsResult.data) setAlerts(alertsResult.data);
        if (interactionsResult.data) setInteractions(interactionsResult.data);
        if (emergencyContactsResult.data) setEmergencyContacts(emergencyContactsResult.data);
        if (providersResult.data) setProviders(providersResult.data);
      }
    } catch (error) {
      console.error('Error fetching member data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRiskBadgeColor = (riskBand: string) => {
    switch (riskBand) {
      case 'Critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'High':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-green-100 text-green-800 border-green-200';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'Critical':
        return 'error';
      case 'High':
        return 'warning';
      case 'Medium':
        return 'info';
      default:
        return 'success';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const navigateToMember = (direction: 'prev' | 'next') => {
    if (!member) return;
    const currentIndex = allMembers.findIndex((m) => m.member_id === member.member_id);
    if (currentIndex === -1) return;

    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < allMembers.length) {
      navigate(`/members/${allMembers[newIndex].member_id}/comprehensive`);
    }
  };

  const canNavigatePrev = () => {
    if (!member || allMembers.length === 0) return false;
    const currentIndex = allMembers.findIndex((m) => m.member_id === member.member_id);
    return currentIndex > 0;
  };

  const canNavigateNext = () => {
    if (!member || allMembers.length === 0) return false;
    const currentIndex = allMembers.findIndex((m) => m.member_id === member.member_id);
    return currentIndex < allMembers.length - 1;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading member data...</p>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Member not found</p>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'eligibility', label: 'Eligibility & Coverage', icon: Shield },
    { id: 'claims', label: 'Claims History', icon: FileText },
    { id: 'utilization', label: 'Utilization', icon: Activity },
    { id: 'authorizations', label: 'Authorizations', icon: FileCheck },
    { id: 'preventive', label: 'Preventive Care', icon: Heart },
    { id: 'alerts', label: 'Care Alerts', icon: Bell },
    { id: 'interactions', label: 'Interactions', icon: MessageSquare },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/members')}
          className="flex items-center text-gray-600 hover:text-blue-600 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Members
        </button>

        <div className="flex items-center space-x-2">
          <button
            onClick={() => navigateToMember('prev')}
            disabled={!canNavigatePrev()}
            className={`p-2 rounded-lg border transition-colors ${
              canNavigatePrev()
                ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                : 'border-gray-200 text-gray-300 cursor-not-allowed'
            }`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-gray-600">
            {allMembers.findIndex((m) => m.member_id === member.member_id) + 1} / {allMembers.length}
          </span>
          <button
            onClick={() => navigateToMember('next')}
            disabled={!canNavigateNext()}
            className={`p-2 rounded-lg border transition-colors ${
              canNavigateNext()
                ? 'border-gray-300 text-gray-700 hover:bg-gray-50'
                : 'border-gray-200 text-gray-300 cursor-not-allowed'
            }`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>

      <Card>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-4">
            <div className="bg-blue-100 p-3 rounded-full">
              <User className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {member.first_name} {member.last_name}
              </h1>
              <p className="text-gray-600 mt-1">ID: {member.member_id}</p>
              <div className="flex items-center space-x-4 mt-3">
                <div className="flex items-center">
                  <Calendar className="w-4 h-4 text-gray-400 mr-1" />
                  <span className="text-sm text-gray-600">DOB: {formatDate(member.date_of_birth)}</span>
                </div>
                <div className="flex items-center">
                  <Globe className="w-4 h-4 text-gray-400 mr-1" />
                  <span className="text-sm text-gray-600">{member.preferred_language}</span>
                </div>
                <div className="flex items-center">
                  {member.consent_on_file ? (
                    <>
                      <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
                      <span className="text-sm text-green-600">Consent on file</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="w-4 h-4 text-amber-500 mr-1" />
                      <span className="text-sm text-amber-600">No consent</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="text-right">
            <div className="flex items-center justify-end space-x-2">
              <span className="text-2xl font-bold text-gray-900">{member.risk_score}</span>
              <Badge className={getRiskBadgeColor(member.risk_band)}>{member.risk_band} Risk</Badge>
            </div>
            <div className="flex items-center justify-end space-x-2 mt-3">
              {member.sdoh_flags.length > 0 && (
                <Badge className="bg-teal-100 text-teal-800 border-teal-200">
                  <Flag className="w-3 h-3 mr-1" />
                  SDoH
                </Badge>
              )}
              {member.behavioral_health_flags.length > 0 && (
                <Badge className="bg-pink-100 text-pink-800 border-pink-200">
                  <Flag className="w-3 h-3 mr-1" />
                  BH
                </Badge>
              )}
            </div>
          </div>
        </div>
      </Card>

      {alerts.length > 0 && (
        <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-red-200">
          <div className="flex items-start">
            <Bell className="w-6 h-6 text-red-600 mr-3 mt-1" />
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900 mb-3">Active Care Alerts ({alerts.length})</h2>
              <div className="space-y-2">
                {alerts.slice(0, 3).map((alert) => (
                  <div key={alert.id} className="flex items-start bg-white rounded-lg p-3 border border-red-200">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <Badge variant={getSeverityColor(alert.severity)}>{alert.severity}</Badge>
                        <span className="text-sm font-medium text-gray-900">{alert.title}</span>
                      </div>
                      <p className="text-sm text-gray-600">{alert.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="border-b border-gray-200 overflow-x-auto">
          <nav className="flex space-x-1 px-4" aria-label="Tabs">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Users className="w-5 h-5 mr-2 text-gray-600" />
                  Emergency Contacts
                </h3>
                <div className="space-y-3">
                  {emergencyContacts.map((contact) => (
                    <div key={contact.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-gray-900">{contact.contact_name}</p>
                        {contact.is_primary && <Badge variant="info">Primary</Badge>}
                      </div>
                      <p className="text-sm text-gray-600">{contact.relationship}</p>
                      <p className="text-sm text-gray-600">{contact.phone_cell}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Stethoscope className="w-5 h-5 mr-2 text-gray-600" />
                  Care Team
                </h3>
                <div className="space-y-3">
                  {providers.map((provider) => (
                    <div key={provider.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-gray-900">{provider.provider_name}</p>
                        {provider.is_primary && <Badge variant="success">Primary</Badge>}
                      </div>
                      <p className="text-sm text-gray-600">{provider.specialty}</p>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-sm text-gray-500">{provider.phone}</p>
                        {provider.last_visit_date && (
                          <p className="text-xs text-gray-500">Last visit: {formatDate(provider.last_visit_date)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MessageSquare className="w-5 h-5 mr-2 text-gray-600" />
                  Recent Interactions
                </h3>
                <div className="space-y-3">
                  {interactions.slice(0, 5).map((interaction) => (
                    <div key={interaction.id} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <Badge variant="info">{interaction.interaction_type}</Badge>
                          <p className="font-medium text-gray-900">{interaction.subject}</p>
                        </div>
                        <p className="text-sm text-gray-500">{formatDate(interaction.interaction_date)}</p>
                      </div>
                      <p className="text-sm text-gray-600">{interaction.notes}</p>
                      <div className="flex items-center justify-between mt-2">
                        <p className="text-xs text-gray-500">By: {interaction.staff_name}</p>
                        <Badge variant="success">{interaction.outcome}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'eligibility' && (
            <div className="space-y-4">
              {eligibility.map((elig) => (
                <Card key={elig.id}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <Shield className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{elig.coverage_type}</h3>
                        <p className="text-sm text-gray-600">{elig.plan_name}</p>
                      </div>
                    </div>
                    <Badge variant={elig.eligibility_status === 'Active' ? 'success' : 'error'}>
                      {elig.eligibility_status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Subscriber ID</p>
                      <p className="text-sm font-medium text-gray-900">{elig.subscriber_id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Effective Date</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(elig.effective_date)}</p>
                    </div>
                    {elig.special_needs.length > 0 && (
                      <div className="col-span-2">
                        <p className="text-sm text-gray-500 mb-1">Special Needs</p>
                        <div className="flex flex-wrap gap-2">
                          {elig.special_needs.map((need, idx) => (
                            <Badge key={idx} variant="warning">{need}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}

          {activeTab === 'claims' && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <CreditCard className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Billed</p>
                      <p className="text-xl font-bold text-gray-900">
                        {formatCurrency(claims.reduce((sum, c) => sum + c.billed_amount, 0))}
                      </p>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <DollarSign className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Paid</p>
                      <p className="text-xl font-bold text-gray-900">
                        {formatCurrency(claims.reduce((sum, c) => sum + c.paid_amount, 0))}
                      </p>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="bg-amber-100 p-2 rounded-lg">
                      <FileText className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Claims</p>
                      <p className="text-xl font-bold text-gray-900">{claims.length}</p>
                    </div>
                  </div>
                </Card>
              </div>
              {claims.map((claim) => (
                <Card key={claim.id}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium text-gray-900">{claim.provider_name}</p>
                      <p className="text-sm text-gray-500">{claim.claim_number}</p>
                    </div>
                    <Badge variant={claim.claim_status === 'Paid' ? 'success' : 'warning'}>
                      {claim.claim_status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Service Date</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(claim.service_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Type</p>
                      <p className="text-sm font-medium text-gray-900">{claim.claim_type}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Billed</p>
                      <p className="text-sm font-medium text-gray-900">{formatCurrency(claim.billed_amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Paid</p>
                      <p className="text-sm font-medium text-gray-900">{formatCurrency(claim.paid_amount)}</p>
                    </div>
                  </div>
                  {claim.diagnosis_codes.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 mb-1">Diagnosis Codes</p>
                      <div className="flex flex-wrap gap-1">
                        {claim.diagnosis_codes.map((code, idx) => (
                          <Badge key={idx} className="bg-gray-100 text-gray-700">{code}</Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}

          {activeTab === 'utilization' && (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4">
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="bg-red-100 p-2 rounded-lg">
                      <Building2 className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Inpatient Stays</p>
                      <p className="text-2xl font-bold text-gray-900">{inpatientStays.length}</p>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="bg-orange-100 p-2 rounded-lg">
                      <AlertTriangle className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">ED Visits</p>
                      <p className="text-2xl font-bold text-gray-900">{edVisits.length}</p>
                    </div>
                  </div>
                </Card>
                <Card>
                  <div className="flex items-center space-x-3">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <Stethoscope className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Office Visits</p>
                      <p className="text-2xl font-bold text-gray-900">{officeVisits.length}</p>
                    </div>
                  </div>
                </Card>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Inpatient Admissions</h3>
                <div className="space-y-3">
                  {inpatientStays.map((stay) => (
                    <Card key={stay.id}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-gray-900">{stay.facility_name}</p>
                        {stay.is_readmission && <Badge variant="error">Readmission</Badge>}
                      </div>
                      <div className="grid grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Admission</p>
                          <p className="text-sm font-medium text-gray-900">{formatDate(stay.admission_date)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Discharge</p>
                          <p className="text-sm font-medium text-gray-900">{formatDate(stay.discharge_date)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">LOS</p>
                          <p className="text-sm font-medium text-gray-900">{stay.length_of_stay} days</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Type</p>
                          <p className="text-sm font-medium text-gray-900">{stay.admission_type}</p>
                        </div>
                      </div>
                      <div className="mt-2">
                        <p className="text-xs text-gray-500">Principal Diagnosis</p>
                        <p className="text-sm text-gray-700">{stay.principal_diagnosis}</p>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Emergency Department Visits</h3>
                <div className="space-y-3">
                  {edVisits.map((visit) => (
                    <Card key={visit.id}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-gray-900">{visit.facility_name}</p>
                        {visit.is_avoidable && <Badge variant="warning">Potentially Avoidable</Badge>}
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Visit Date</p>
                          <p className="text-sm font-medium text-gray-900">{formatDate(visit.visit_date)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Chief Complaint</p>
                          <p className="text-sm font-medium text-gray-900">{visit.chief_complaint}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Disposition</p>
                          <p className="text-sm font-medium text-gray-900">{visit.disposition}</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'authorizations' && (
            <div className="space-y-4">
              {authorizations.map((auth) => (
                <Card key={auth.id}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium text-gray-900">{auth.service_type}</p>
                      <p className="text-sm text-gray-600">{auth.service_description}</p>
                      <p className="text-sm text-gray-500 mt-1">{auth.authorization_number}</p>
                    </div>
                    <Badge
                      variant={
                        auth.authorization_status === 'Approved'
                          ? 'success'
                          : auth.authorization_status === 'Denied'
                          ? 'error'
                          : 'warning'
                      }
                    >
                      {auth.authorization_status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-500">Request Date</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(auth.request_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Expiration</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(auth.expiration_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Units Approved</p>
                      <p className="text-sm font-medium text-gray-900">{auth.units_approved}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Units Used</p>
                      <p className="text-sm font-medium text-gray-900">{auth.units_used}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {activeTab === 'preventive' && (
            <div className="space-y-4">
              {preventiveCare.map((item) => (
                <Card key={item.id}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className={`p-2 rounded-lg ${item.gap_in_care ? 'bg-red-100' : 'bg-green-100'}`}>
                        <Heart className={`w-5 h-5 ${item.gap_in_care ? 'text-red-600' : 'text-green-600'}`} />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{item.service_name}</p>
                        <p className="text-sm text-gray-500">{item.service_type}</p>
                      </div>
                    </div>
                    {item.gap_in_care ? (
                      <Badge variant="error">Gap in Care</Badge>
                    ) : (
                      <Badge variant="success">Compliant</Badge>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-gray-500">Last Service Date</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(item.service_date)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Next Due Date</p>
                      <p className="text-sm font-medium text-gray-900">{formatDate(item.next_due_date)}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {activeTab === 'alerts' && (
            <div className="space-y-4">
              {alerts.map((alert) => (
                <Card key={alert.id} className="border-l-4 border-l-red-500">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <Badge variant={getSeverityColor(alert.severity)}>{alert.severity}</Badge>
                        <Badge className="bg-gray-100 text-gray-700">{alert.alert_type}</Badge>
                      </div>
                      <h3 className="font-semibold text-gray-900">{alert.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{alert.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                    <p className="text-xs text-gray-500">Created: {formatDate(alert.created_at)}</p>
                    <Badge variant="info">{alert.status}</Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}

          {activeTab === 'interactions' && (
            <div className="space-y-4">
              {interactions.map((interaction) => (
                <Card key={interaction.id}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Badge variant="info">{interaction.interaction_type}</Badge>
                      <p className="font-medium text-gray-900">{interaction.subject}</p>
                    </div>
                    <p className="text-sm text-gray-500">{formatDate(interaction.interaction_date)}</p>
                  </div>
                  <p className="text-sm text-gray-600 mb-3">{interaction.notes}</p>
                  <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                    <p className="text-sm text-gray-500">By: {interaction.staff_name}</p>
                    <Badge variant="success">{interaction.outcome}</Badge>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
